import React, { useEffect, useState } from 'react'

/**
 * CaseCloseModal — SVCP §5.2 structured decision-reasoning gate.
 *
 * Mount wherever a case may transition to CLOSED after investigation has
 * started. The modal collects a resolution_status (from the 5-value enum
 * declared in case_management_schema.sql / cases.js) and a narrative of
 * at least 20 characters before resolving its promise.
 *
 * Usage:
 *   <CaseCloseModal
 *     open={showClose}
 *     previousStatus={caseData.status}
 *     onCancel={() => setShowClose(false)}
 *     onConfirm={({ resolution_status, reason }) => {
 *        setShowClose(false)
 *        await putCaseStatus({ status: 'CLOSED', resolution_status, reason })
 *     }}
 *   />
 *
 * If the case is still in OPEN, the backend doesn't require a structured
 * resolution — callers can bypass this modal entirely in that path.
 */

// Must stay in sync with CLOSE_RESOLUTION_STATUSES in backend/src/routes/cases.js
// and the resolution_status enum in case_management_schema.sql.
export const CASE_RESOLUTION_OPTIONS = [
  {
    value: 'FALSE_POSITIVE',
    label: 'False positive',
    blurb: 'Rule fired but the activity had a benign, documented explanation.',
  },
  {
    value: 'CONFIRMED_ISSUE',
    label: 'Confirmed issue — managed internally',
    blurb: 'Concerning behaviour confirmed; addressed via internal controls without a regulator report.',
  },
  {
    value: 'NEEDS_MONITORING',
    label: 'Needs monitoring',
    blurb: 'Not suspicious yet; patron placed on a watchlist / monitoring plan for follow-up.',
  },
  {
    value: 'REGULATORY_REPORT',
    label: 'Regulatory report — SMR / TTR handled',
    blurb: 'The regulatory report was lodged (or is being lodged) via the separate FILED path. Use when closing a parent/linked case.',
  },
  {
    value: 'ACCOUNT_ACTION',
    label: 'Account action taken',
    blurb: 'Patron account suspended, restricted, or terminated in response to the case findings.',
  },
]

const MIN_NARRATIVE_CHARS = 20

export default function CaseCloseModal({
  open,
  previousStatus,
  onCancel,
  onConfirm,
  submitting = false,
}) {
  const [resolutionStatus, setResolutionStatus] = useState('')
  const [reason, setReason] = useState('')

  useEffect(() => {
    if (!open) {
      setResolutionStatus('')
      setReason('')
    }
  }, [open])

  if (!open) return null

  const trimmedLen = reason.trim().length
  const canSubmit =
    !submitting && !!resolutionStatus && trimmedLen >= MIN_NARRATIVE_CHARS

  const activeOption = CASE_RESOLUTION_OPTIONS.find((o) => o.value === resolutionStatus)

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15, 23, 42, 0.55)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: 20,
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 560,
          background: '#fff',
          borderRadius: 12,
          boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
          overflow: 'hidden',
        }}
      >
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #e2e8f0' }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#0f172a' }}>
            Close this case
          </div>
          <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
            SVCP §5.2 — closing after {previousStatus || 'investigation'} requires a
            structured resolution and a narrative of at least {MIN_NARRATIVE_CHARS} characters.
          </div>
        </div>

        <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label
              style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#334155', marginBottom: 6 }}
            >
              Resolution <span style={{ color: '#b91c1c' }}>*</span>
            </label>
            <select
              value={resolutionStatus}
              onChange={(e) => setResolutionStatus(e.target.value)}
              disabled={submitting}
              style={{
                width: '100%',
                padding: 8,
                fontSize: 13,
                borderRadius: 6,
                border: '1px solid #cbd5e1',
                background: '#fff',
                fontFamily: 'inherit',
              }}
            >
              <option value="">Select a resolution…</option>
              {CASE_RESOLUTION_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            {activeOption && (
              <div style={{ fontSize: 12, color: '#64748b', marginTop: 6 }}>
                {activeOption.blurb}
              </div>
            )}
          </div>

          <div>
            <div
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}
            >
              <label style={{ fontSize: 12, fontWeight: 600, color: '#334155' }}>
                Decision narrative <span style={{ color: '#b91c1c' }}>*</span>
              </label>
              <span
                style={{
                  fontSize: 11,
                  fontFamily: 'monospace',
                  color: trimmedLen >= MIN_NARRATIVE_CHARS ? '#16a34a' : '#94a3b8',
                }}
              >
                {trimmedLen} / {MIN_NARRATIVE_CHARS} min
              </span>
            </div>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={submitting}
              rows={4}
              placeholder="What did you find, what decisions did you take, and why is closing without escalation appropriate?"
              style={{
                width: '100%',
                padding: 10,
                fontSize: 13,
                borderRadius: 6,
                border: '1px solid #cbd5e1',
                fontFamily: 'inherit',
                boxSizing: 'border-box',
                resize: 'vertical',
              }}
            />
            {trimmedLen > 0 && trimmedLen < MIN_NARRATIVE_CHARS && (
              <div style={{ fontSize: 11, color: '#b91c1c', marginTop: 4 }}>
                Minimum {MIN_NARRATIVE_CHARS} characters required.
              </div>
            )}
          </div>
        </div>

        <div
          style={{
            padding: '12px 20px',
            borderTop: '1px solid #e2e8f0',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 8,
            background: '#f8fafc',
          }}
        >
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            style={{
              padding: '8px 16px',
              fontSize: 13,
              fontWeight: 600,
              color: '#334155',
              background: '#fff',
              border: '1px solid #cbd5e1',
              borderRadius: 6,
              cursor: submitting ? 'not-allowed' : 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => {
              if (!canSubmit) return
              onConfirm({
                resolution_status: resolutionStatus,
                reason: reason.trim(),
              })
            }}
            disabled={!canSubmit}
            style={{
              padding: '8px 16px',
              fontSize: 13,
              fontWeight: 700,
              color: '#fff',
              background: canSubmit ? '#b45309' : '#cbd5e1',
              border: 'none',
              borderRadius: 6,
              cursor: canSubmit ? 'pointer' : 'not-allowed',
            }}
          >
            {submitting ? 'Closing…' : 'Close case'}
          </button>
        </div>
      </div>
    </div>
  )
}
